(() => {
  const WRAPPER_SELECTOR = '[data-scrptd-express-checkout]';
  const HIDDEN_CLASS = 'scrptd-express-provider-hidden';
  const PROVIDERS = {
    shop_pay: /\bshop\s*pay\b|\bshopify\s*pay\b/i,
    apple_pay: /\bapple\s*pay\b/i,
    paypal: /\bpay\s*pal\b|\bpaypal\b/i,
    google_pay: /\bgoogle\s*pay\b|\bgpay\b/i
  };
  const BUTTON_SELECTOR = [
    'button',
    'a',
    'iframe',
    '[role="button"]',
    '[data-testid]',
    '[data-payment-method]',
    '[data-wallet-name]',
    '[class*="additional-checkout-button"]'
  ].join(',');

  const descriptor = (node) => {
    if (!(node instanceof Element)) return '';
    const values = [
      node.tagName,
      node.id,
      typeof node.className === 'string' ? node.className : '',
      node.getAttribute('aria-label'),
      node.getAttribute('title'),
      node.getAttribute('name'),
      node.getAttribute('value'),
      node.getAttribute('alt'),
      node.getAttribute('src'),
      node.getAttribute('data-testid'),
      node.getAttribute('data-payment-method'),
      node.getAttribute('data-wallet-name'),
      node.textContent
    ];
    return values.filter(Boolean).join(' ');
  };

  const providerFor = (node) => {
    const haystack = descriptor(node);
    return Object.keys(PROVIDERS).find((provider) => PROVIDERS[provider].test(haystack)) || '';
  };

  const buttonTarget = (node, root) => {
    if (!(node instanceof Element)) return null;
    let target = node.closest('button, a, iframe, [role="button"], li, [class*="additional-checkout-button"]') || node;
    if (target.tagName === 'IFRAME' && target.parentElement && target.parentElement !== root) {
      target = target.parentElement;
    }
    const listItem = target.closest('li');
    if (listItem && root.contains(listItem)) target = listItem;
    return target === root ? null : target;
  };

  const rootsWithin = (wrapper) => {
    const roots = [wrapper];
    const seen = new Set(roots);
    for (let index = 0; index < roots.length; index += 1) {
      const root = roots[index];
      root.querySelectorAll('*').forEach((node) => {
        if (node.shadowRoot && !seen.has(node.shadowRoot)) {
          seen.add(node.shadowRoot);
          roots.push(node.shadowRoot);
        }
      });
    }
    return roots;
  };

  const providerOrder = (wrapper) => {
    const configured = String(wrapper.dataset.expressProviders || '')
      .split(',')
      .map((provider) => provider.trim())
      .filter((provider) => provider && provider !== 'hidden' && PROVIDERS[provider]);
    return [...new Set(configured)];
  };

  const setReady = (wrapper, state = 'ready') => {
    wrapper.classList.remove('is-filtering');
    wrapper.dataset.expressFilterState = state;
  };

  const filterWrapper = (wrapper, forceReady = false) => {
    if (!(wrapper instanceof Element)) return false;
    const allowed = providerOrder(wrapper);
    const order = new Map(allowed.map((provider, index) => [provider, index]));
    let identified = 0;
    let visible = 0;

    rootsWithin(wrapper).forEach((root) => {
      const handledTargets = new Set();
      root.querySelectorAll(BUTTON_SELECTOR).forEach((node) => {
        const provider = providerFor(node);
        if (!provider) return;
        const target = buttonTarget(node, root);
        if (!target || handledTargets.has(target)) return;
        handledTargets.add(target);
        target.dataset.scrptdExpressHandled = provider;
        identified += 1;
        if (!order.has(provider)) {
          target.classList.add(HIDDEN_CLASS);
          target.setAttribute('aria-hidden', 'true');
          return;
        }
        target.classList.remove(HIDDEN_CLASS);
        target.removeAttribute('aria-hidden');
        target.style.setProperty('--scrptd-express-order', String(order.get(provider)));
        target.style.order = String(order.get(provider));
        visible += 1;
      });
    });

    if (!allowed.length) {
      wrapper.hidden = true;
      setReady(wrapper, 'empty');
      return true;
    }

    wrapper.hidden = false;
    if (identified || allowed.length === Object.keys(PROVIDERS).length) {
      setReady(wrapper, identified ? 'filtered' : 'native');
      if (identified && !visible) wrapper.hidden = true;
      return true;
    }
    if (forceReady) {
      const nativeElement = wrapper.querySelector('shopify-accelerated-checkout-cart, shopify-accelerated-checkout');
      const nativeHeight = Math.max(
        Number(nativeElement?.getBoundingClientRect().height) || 0,
        Number(nativeElement?.scrollHeight) || 0
      );
      const expectedHeight = (allowed.length * 54) + (Math.max(0, allowed.length - 1) * 8) + 8;
      const countMatches = nativeHeight > 0 && nativeHeight <= expectedHeight;
      wrapper.hidden = !countMatches;
      setReady(wrapper, countMatches ? 'native-count-match' : 'unavailable');
      if (!countMatches) {
        console.warn('SCRPTD Express Checkout: Shopify liefert mehr oder nicht prüfbare Anbieter als im Theme gewählt. Der Block wurde ausgeblendet.');
      }
      return true;
    }
    return false;
  };

  const initialise = (wrapper) => {
    if (!(wrapper instanceof Element) || wrapper.dataset.scrptdExpressInitialised === 'true') return;
    wrapper.dataset.scrptdExpressInitialised = 'true';
    wrapper.classList.add('is-filtering');
    wrapper.dataset.expressFilterState = 'pending';

    let attempts = 0;
    let timer = 0;
    const scan = () => {
      window.clearTimeout(timer);
      attempts += 1;
      const settled = filterWrapper(wrapper, attempts >= 30);
      if (!settled && attempts < 30) timer = window.setTimeout(scan, 100);
    };

    const observer = new MutationObserver(() => {
      wrapper.classList.add('is-filtering');
      wrapper.dataset.expressFilterState = 'pending';
      attempts = 0;
      window.clearTimeout(timer);
      timer = window.setTimeout(scan, 0);
    });
    observer.observe(wrapper, { childList: true, subtree: true });

    if (window.customElements?.whenDefined) {
      window.customElements.whenDefined('shopify-accelerated-checkout').then(scan);
      window.customElements.whenDefined('shopify-accelerated-checkout-cart').then(scan);
    }
    scan();
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(WRAPPER_SELECTOR)) initialise(scope);
    scope.querySelectorAll?.(WRAPPER_SELECTOR).forEach(initialise);
  };

  initialiseAll();
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
  document.addEventListener('scrptd:cart-change', () => initialiseAll());
  document.addEventListener('cart:updated', () => initialiseAll());
})();
